/**
 * Public-page analytics tracker.
 *
 * - Generates a stable anonymous `visitor_id` (UUID) in localStorage on first visit.
 * - Maintains a `session_id` with a 30-minute idle window in sessionStorage.
 * - Tracks page-views via POST /api/analytics/track on every route change.
 * - NEVER fires when the path starts with /admin (defence in depth — the backend
 *   also strips admin paths server-side).
 *
 * Owner identification is done retroactively from the Admin → Analytics →
 * Visitors tab via the "Mark as me" button (POST /admin/analytics/visitors/:id/mark-owner).
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import api, { API } from "@/lib/api";

const VISITOR_KEY = "sc_visitor_id";
const SESSION_KEY = "sc_session";
const SESSION_IDLE_MS = 30 * 60 * 1000; // 30 minutes

// Hostnames that always represent agent/preview/dev traffic, never real users.
// Any pageview from these is dropped entirely (never even sent to the backend)
// so the analytics DB only contains genuine public-internet visitors.
const EMERGENT_HOST_SUFFIXES = [
  ".emergentagent.com",
  ".emergent.com",
  ".emergent.sh",
  ".emergent.host",
];

// When the admin token is present in localStorage we treat this browser as
// the site owner. Tracking from a preview / emergent host is allowed in
// that case (so the admin can verify their own analytics during testing),
// but the row is flagged `is_owner: true` so it stays out of the "real
// visitor" counts unless the "Include my own visits" toggle is on.
const ADMIN_TOKEN_KEY = "sc_admin_token";

function isAdminBrowser() {
  try {
    return Boolean(localStorage.getItem(ADMIN_TOKEN_KEY));
  } catch {
    return false;
  }
}

function isEmergentTraffic() {
  if (typeof window === "undefined") return false;
  const host = (window.location.hostname || "").toLowerCase();
  if (!host || host === "localhost" || host === "127.0.0.1") return false;
  return EMERGENT_HOST_SUFFIXES.some((s) => host === s.slice(1) || host.endsWith(s));
}

function uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "v-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// IANA timezone of the visitor's browser (e.g. "Europe/Lisbon"). Used as a
// fallback for country attribution when the request is proxied through a
// Cloudflare Worker that strips ``cf-connecting-ip``.
function getTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

function getVisitorId() {
  try {
    let v = localStorage.getItem(VISITOR_KEY);
    if (!v) {
      v = uuid();
      localStorage.setItem(VISITOR_KEY, v);
    }
    return v;
  } catch {
    return "anon-" + Date.now();
  }
}

function getSessionId() {
  const now = Date.now();
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) {
      const { id, ts } = JSON.parse(raw);
      if (id && now - ts < SESSION_IDLE_MS) {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify({ id, ts: now }));
        return id;
      }
    }
    const id = uuid();
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ id, ts: now }));
    return id;
  } catch {
    return "s-" + now;
  }
}

export function useAnalytics() {
  const { pathname } = useLocation();
  const lastTracked = useRef("");

  useEffect(() => {
    if (pathname.startsWith("/admin")) return;
    // Drop everything from preview / agent / dev hosts EXCEPT when the admin
    // is logged in — in that case we still record so the admin can verify
    // their own analytics from the preview URL, with `is_owner: true` so the
    // row is hidden from default counts.
    const adminBrowser = isAdminBrowser();
    if (isEmergentTraffic() && !adminBrowser) return;
    const key = pathname + "::" + (typeof document !== "undefined" ? document.title : "");
    if (lastTracked.current === key) return;
    lastTracked.current = key;

    const payload = {
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
      path: pathname,
      referrer: typeof document !== "undefined" ? document.referrer || null : null,
      is_owner: adminBrowser,
      screen: typeof window !== "undefined" && window.screen
        ? `${window.screen.width}x${window.screen.height}`
        : null,
      timezone: getTimezone(),
    };

    // Fire-and-forget; never let a tracking failure surface to the UI.
    api.post("/analytics/track", payload).catch(() => {});
  }, [pathname]);
}

/**
 * Track an outbound click (a click on a "Visit website" / "View on brand"
 * link that takes the visitor away from our site). Returns a Promise that
 * resolves after the request is fired so the caller can safely navigate
 * afterwards — but is also safe to call without awaiting.
 *
 * @param {Object} args
 * @param {"brand"|"clothing"} args.target_type
 * @param {string} args.target_slug
 * @param {string} args.destination_url
 */
export function trackOutboundClick({ target_type, target_slug, destination_url }) {
  if (!target_type || !target_slug || !destination_url) return Promise.resolve();
  const adminBrowser = isAdminBrowser();
  // Same rule as page-views: drop preview / emergent traffic unless this is
  // the admin testing, in which case we record with is_owner: true.
  if (isEmergentTraffic() && !adminBrowser) return Promise.resolve();
  const payload = {
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    target_type,
    target_slug,
    destination_url,
    referrer: typeof document !== "undefined" ? document.referrer || null : null,
    is_owner: adminBrowser,
    timezone: getTimezone(),
  };
  // Use sendBeacon when available so the tracking survives a same-tab navigation
  // away from our page. Fall back to a fire-and-forget fetch otherwise.
  try {
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      const url = `${API}/analytics/track-outbound`;
      const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return Promise.resolve();
    }
  } catch { /* swallow, fall through */ }
  return api.post("/analytics/track-outbound", payload).catch(() => {});
}
